import React, { useState } from "react";
import axios from "../../../api/axios";
import {
  Avatar,
  CircularProgress,
  Grid,
  InputAdornment,
  Tooltip,
  Typography,
  Box,
} from "@mui/material";
import { AccountCircle, Groups, MonetizationOn, Work } from "@mui/icons-material";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import PageShell, { SectionCard } from "components/patch/PageShell";

const StaffProfile = () => {
  const [pf_no, setPfno] = useState("");
  const [staff, setStaff] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchStaffDetails = async () => {
    if (!pf_no.trim()) {
      setError("Please enter a PF No.");
      return;
    }

    setLoading(true);
    setError("");
    setStaff(null);

    try {
      const response = await axios.get("/staff/details/pfno", {
        params: { pf_no: pf_no.trim() },
      });
      setStaff(response.data);
    } catch (err) {
      setError(err?.response?.data?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const annualPay =
    staff?.amount && !Number.isNaN(Number(staff.amount)) ? Number(staff.amount) * 12 : null;

  return (
    <PageShell
      title="Staff Profile"
      subtitle="Search by PF number and view detailed employee information."
    >
      <SectionCard title="Profile Search" subtitle="Enter a PF number to retrieve staff details.">
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <MDInput
              label="PF No"
              fullWidth
              value={pf_no}
              onChange={(e) => setPfno(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AccountCircle fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <MDButton
              variant="gradient"
              color="error"
              fullWidth
              onClick={fetchStaffDetails}
              disabled={loading}
            >
              {loading ? "Searching..." : "Search Profile"}
            </MDButton>
          </Grid>
        </Grid>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : null}

        {error ? (
          <Typography sx={{ mt: 2, color: "#b42318", fontSize: "0.92rem", fontWeight: 600 }}>
            {error}
          </Typography>
        ) : null}
      </SectionCard>

      {staff ? (
        <Box mt={3}>
          <SectionCard title="Employee Profile" subtitle="Payroll and employment details.">
            <Grid container spacing={3}>
              <Grid item xs={12} md={3} display="flex" justifyContent="center" alignItems="center">
                <Avatar
                  sx={{
                    width: 96,
                    height: 96,
                    bgcolor: "#c81e1e",
                    fontSize: 38,
                    fontWeight: 700,
                    boxShadow: "0 14px 28px rgba(200, 30, 30, 0.18)",
                  }}
                >
                  {staff?.firstname?.charAt(0) || "S"}
                </Avatar>
              </Grid>

              <Grid item xs={12} md={9}>
                <Typography
                  sx={{
                    fontSize: "1.35rem",
                    fontWeight: 700,
                    letterSpacing: "-0.02em",
                    color: "#10284f",
                    mb: 2,
                  }}
                >
                  {staff?.surname || "-"} {staff?.firstname || "-"} {staff?.othername || ""}
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Tooltip title={staff?.pf_no || "-"}>
                      <Typography sx={{ mb: 1.25 }}>
                        <strong>PF No:</strong> {staff?.pf_no || "-"}
                      </Typography>
                    </Tooltip>

                    <Tooltip title={staff?.designation_name || "-"}>
                      <Typography sx={{ mb: 1.25 }}>
                        <Work fontSize="small" sx={{ verticalAlign: "middle", mr: 0.75 }} />
                        <strong>Designation:</strong> {staff?.designation_name || "-"}
                      </Typography>
                    </Tooltip>

                    <Tooltip title={staff?.cadre_name || "-"}>
                      <Typography sx={{ mb: 1.25 }}>
                        <strong>Cadre:</strong> {staff?.cadre_name || "-"}
                      </Typography>
                    </Tooltip>

                    <Tooltip title={staff?.grade_level || "-"}>
                      <Typography sx={{ mb: 1.25 }}>
                        <strong>Grade Level:</strong> {staff?.grade_level || "-"}
                      </Typography>
                    </Tooltip>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Tooltip title={staff?.staff_group || "-"}>
                      <Typography sx={{ mb: 1.25 }}>
                        <Groups fontSize="small" sx={{ verticalAlign: "middle", mr: 0.75 }} />
                        <strong>Staff Group:</strong> {staff?.staff_group || "-"}
                      </Typography>
                    </Tooltip>

                    <Tooltip title={annualPay ? annualPay.toLocaleString() : "N/A"}>
                      <Typography sx={{ mb: 1.25 }}>
                        <MonetizationOn
                          fontSize="small"
                          sx={{ verticalAlign: "middle", mr: 0.75 }}
                        />
                        <strong>Annual Pay:</strong>{" "}
                        {annualPay ? annualPay.toLocaleString() : "N/A"}
                      </Typography>
                    </Tooltip>

                    <Tooltip title={staff?.salary_structure || "-"}>
                      <Typography sx={{ mb: 1.25 }}>
                        <strong>Salary Structure:</strong> {staff?.salary_structure || "-"}
                      </Typography>
                    </Tooltip>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </SectionCard>
        </Box>
      ) : null}
    </PageShell>
  );
};

export default StaffProfile;
